// Shared geometry helpers for place suggestions.
//
// Suggestion polygons arrive as GeoJSON ([lng, lat] order). These helpers
// normalize the raw payload, walk its points, and derive a bounding box /
// centroid — used by the suggestion card, the map layer, and the geofence
// create view so the polygon math lives in exactly one place.

export type SuggestionPolygon = {
  type: 'Polygon';
  coordinates: number[][][];
};

export type SuggestionMultiPolygon = {
  type: 'MultiPolygon';
  coordinates: number[][][][];
};

export type SuggestionGeometry = SuggestionPolygon | SuggestionMultiPolygon;

export type GeometryBounds = {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
};

/**
 * Coerce a raw polygon payload into a GeoJSON Polygon/MultiPolygon object.
 * Accepts either an already-shaped `{ type, coordinates }` geometry or a bare
 * coordinates array, inferring Polygon vs MultiPolygon from nesting depth.
 * Returns `null` for anything that isn't a (multi)polygon.
 */
export function normalizePolygon(p: unknown): SuggestionGeometry | null {
  if (!p) return null;

  const maybeGeom = p as { type?: string; coordinates?: unknown };
  if (maybeGeom.type && maybeGeom.coordinates) {
    if (maybeGeom.type === 'Polygon' || maybeGeom.type === 'MultiPolygon') {
      return maybeGeom as SuggestionGeometry;
    }
    return null;
  }

  if (!Array.isArray(p) || !Array.isArray(p[0])) return null;
  // Polygon coords: [ring][point][lng,lat] — depth 3
  // MultiPolygon coords: [poly][ring][point][lng,lat] — depth 4
  if (Array.isArray(p[0][0]) && Array.isArray(p[0][0][0])) {
    return { type: 'MultiPolygon', coordinates: p as number[][][][] };
  }
  if (Array.isArray(p[0][0])) {
    return { type: 'Polygon', coordinates: p as number[][][] };
  }
  return null;
}

/** Flatten a geometry to a flat list of `[lng, lat]` points. */
export function collectPoints(geom: SuggestionGeometry | null): number[][] {
  if (!geom) return [];
  if (geom.type === 'Polygon') return geom.coordinates.flat();
  if (geom.type === 'MultiPolygon') return geom.coordinates.flat(2);
  return [];
}

/** Axis-aligned bounding box of a geometry, or `null` when it has no points. */
export function geometryBounds(
  geom: SuggestionGeometry | null,
): GeometryBounds | null {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const point of collectPoints(geom)) {
    const [lng, lat] = point;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }

  if (minLat === Infinity) return null;
  return { minLat, maxLat, minLng, maxLng };
}

/** Bounding-box centroid as `[lat, lng]`, or `null` when the geometry is empty. */
export function geometryCenter(
  geom: SuggestionGeometry | null,
): [number, number] | null {
  const bounds = geometryBounds(geom);
  if (!bounds) return null;
  return [
    (bounds.minLat + bounds.maxLat) / 2,
    (bounds.minLng + bounds.maxLng) / 2,
  ];
}
